import type {
  CapabilitiesSection, CertificationsSection, IndustriesSection, PackagesSection, ProcessSection, ServicesSection,
  SpecialtiesSection, StorySection, TeamSection, TestimonialsSection,
} from "../../content/industry";
import { Icon } from "./Icon";
import { Photo } from "./Photo";
import { SectionHeading } from "./SectionHeading";

/** Industrias atendidas: retícula numerada con líneas visibles. */
export function Industries({ section }: { section: IndustriesSection }) {
  return (
    <section id={section.id} className="ix-section ix-industries" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
        <ul className="ix-grid ix-grid--3 ix-lined">
          {section.items.map((item, index) => <li key={item.name}>
            <span className="ix-index">{String(index + 1).padStart(2, "0")}</span>
            <h3>{item.name}</h3><p>{item.text}</p>
          </li>)}
        </ul>
      </div>
    </section>
  );
}

/** Capacidades técnicas: celdas con código y especificaciones, sobre banda oscura. */
export function Capabilities({ section }: { section: CapabilitiesSection }) {
  return (
    <section id={section.id} className="ix-section ix-band-dark ix-capabilities" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        <div className="ix-grid ix-grid--4">
          {section.items.map(item => <article key={item.code} className="ix-cell">
            <span className="ix-code">{item.code}</span>
            <h3>{item.title}</h3><p>{item.text}</p>
            <ul className="ix-specs">{item.specs.map(spec => <li key={spec}>{spec}</li>)}</ul>
          </article>)}
        </div>
      </div>
    </section>
  );
}

/** Proceso o línea de tiempo con imagen opcional. */
export function Process({ section }: { section: ProcessSection }) {
  return (
    <section id={section.id} className="ix-section ix-process" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container ix-split">
        <div>
          <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
          <ol className="ix-steps">{section.steps.map((step, index) => <li key={step.title}>
            <span className="ix-step-number" aria-hidden="true">{index + 1}</span>
            <div><h3>{step.title}</h3><p>{step.text}</p></div>
          </li>)}</ol>
        </div>
        {section.image !== undefined ? <Photo index={section.image} className="ix-process-photo" sizes="(min-width: 1024px) 45vw, 100vw" /> : null}
      </div>
    </section>
  );
}

/** Certificaciones: el aviso de ejemplo se muestra siempre hasta que lo cambies en el contenido. */
export function Certifications({ section }: { section: CertificationsSection }) {
  return (
    <section id={section.id} className="ix-section ix-surface ix-certifications" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        <ul className="ix-grid ix-grid--4 ix-badges">{section.items.map(item => <li key={item.name}>
          {item.icon ? <span className="ix-badge-icon"><Icon name={item.icon} size={20} /></span> : null}
          <strong>{item.name}</strong><span>{item.text}</span>
        </li>)}</ul>
        <p className="ix-notice" role="note">{section.note}</p>
      </div>
    </section>
  );
}

/** Servicios: lista editorial con duración y precio, o tarjetas. */
export function Services({ section }: { section: ServicesSection }) {
  return (
    <section id={section.id} className={`ix-section ix-services ix-services--${section.variant}`} aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        <ul className="ix-service-list">{section.items.map(item => <li key={item.name}>
          <div className="ix-service-line"><h3>{item.name}</h3><span aria-hidden="true" className="ix-leader" />{item.price ? <strong>{item.price}</strong> : null}</div>
          <p>{item.text}</p>{item.duration ? <span className="ix-chip">{item.duration}</span> : null}
        </li>)}</ul>
        {section.note ? <p className="ix-note">{section.note}</p> : null}
      </div>
    </section>
  );
}

/** Paquetes o planes; el destacado invierte colores. */
export function Packages({ section }: { section: PackagesSection }) {
  return (
    <section id={section.id} className="ix-section ix-surface ix-packages" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
        <div className="ix-grid ix-grid--3">{section.items.map(item => <article key={item.name} className={item.featured ? "ix-package is-featured" : "ix-package"}>
          {item.featured ? <span className="ix-chip">Más elegido</span> : null}
          <h3>{item.name}</h3><p className="ix-price">{item.price}</p><p>{item.text}</p>
          <ul className="ix-specs">{item.features.map(feature => <li key={feature}>{feature}</li>)}</ul>
        </article>)}</div>
        {section.note ? <p className="ix-note">{section.note}</p> : null}
      </div>
    </section>
  );
}

/** Equipo con iniciales (sin fotos de personas que no autorizaron su imagen). */
export function Team({ section }: { section: TeamSection }) {
  return (
    <section id={section.id} className="ix-section ix-team" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
        <ul className="ix-grid ix-grid--3">{section.items.map(person => <li key={person.name} className="ix-person">
          <span className="ix-avatar" aria-hidden="true">{person.name.split(" ").map(part => part[0]).slice(0, 2).join("")}</span>
          <h3>{person.name}</h3><p className="ix-role">{person.role}</p><p>{person.text}</p>
        </li>)}</ul>
      </div>
    </section>
  );
}

export function Testimonials({ section }: { section: TestimonialsSection }) {
  return (
    <section id={section.id} className="ix-section ix-surface ix-testimonials" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
        <div className="ix-grid ix-grid--3">{section.items.map(item => <figure key={item.quote} className="ix-quote">
          {item.rating ? <span className="ix-rating" role="img" aria-label={`${item.rating} de 5 estrellas`}>{Array.from({ length: Math.min(5, item.rating) }, (_, star) => <Icon key={star} name="star" size={16} className="ix-star" />)}</span> : null}
          <blockquote><p>{item.quote}</p></blockquote>
          <figcaption><strong>{item.author}</strong> · {item.detail}</figcaption>
        </figure>)}</div>
        <p className="ix-notice" role="note">{section.note}</p>
      </div>
    </section>
  );
}

export function Story({ section }: { section: StorySection }) {
  return (
    <section id={section.id} className="ix-section ix-story" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container ix-split">
        <Photo index={section.image} className="ix-story-photo" sizes="(min-width: 1024px) 40vw, 100vw" />
        <div>
          <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
          {section.paragraphs.map(text => <p key={text} className="ix-body">{text}</p>)}
          {section.signature ? <p className="ix-signature">{section.signature}</p> : null}
        </div>
      </div>
    </section>
  );
}

export function Specialties({ section }: { section: SpecialtiesSection }) {
  return (
    <section id={section.id} className="ix-section ix-specialties" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
        <div className="ix-grid ix-grid--3">{section.items.map(item => <article key={item.name} className="ix-dish">
          <Photo index={item.image} sizes="(min-width: 1024px) 33vw, 100vw" />
          <div className="ix-dish-body"><h3>{item.name}</h3><p>{item.text}</p><strong className="ix-price">{item.price}</strong></div>
        </article>)}</div>
      </div>
    </section>
  );
}
